import { callable } from './callable'
import { createChart } from './chartService'
import type { ChartSeries, ChartSpec } from '../types/chart'

export type ChartFromTextResult =
  | { ok: true; spec: ChartSpec }
  | { ok: false; reason: 'CANT_CHART' | 'NOT_AVAILABLE_YET' | 'NO_ACCESS' }

export type ExplainChartResult =
  | { ok: true; text: string }
  | { ok: false; reason: string }

export type SuggestChartsResult =
  | { ok: true; specs: ChartSpec[]; free: boolean }
  | { ok: false; reason: 'FREE_USED' | 'CANT_CHART' }

/**
 * Drafts a single ChartSpec from natural language text using Vertex AI.
 */
export async function draftChartFromText(
  workspaceId: string,
  text: string,
): Promise<ChartFromTextResult> {
  return callable<{ workspaceId: string; text: string }, ChartFromTextResult>(
    'chartFromText',
    { workspaceId, text },
  )
}

/**
 * Explains a chart's computed series points in plain English (CC-P9 sanitized).
 */
export async function explainChart(
  workspaceId: string,
  spec: ChartSpec,
  series: ChartSeries[],
): Promise<ExplainChartResult> {
  return callable<{ workspaceId: string; spec: ChartSpec; series: ChartSeries[] }, ExplainChartResult>(
    'explainChart',
    { workspaceId, spec, series },
  )
}

/**
 * Recommends up to 6 charts for the workspace based on catalog and document counts.
 */
export async function suggestCharts(
  workspaceId: string,
  mode: 'auto' | 'request' = 'request',
): Promise<SuggestChartsResult> {
  return callable<{ workspaceId: string; mode: 'auto' | 'request' }, SuggestChartsResult>(
    'suggestCharts',
    { workspaceId, mode },
  )
}

/**
 * Saves a draft ChartSpec to the workspace library via createChart (enforcing maxSavedCharts).
 */
export async function saveDraftToLibrary(
  workspaceId: string,
  spec: ChartSpec,
): Promise<{ chartId: string }> {
  return createChart(workspaceId, spec)
}
